import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "../../Components/Layout/SidebarLayout";
import config from "../../config";
import { useAuthenticatedFetch } from "../../Hooks/useFetch";
import { BsArrowRight, BsPencil } from "react-icons/bs";
import { format } from "date-fns";

interface Quiz {
  name: string;
  authorId: number;
  id: number;
  timeCreated: string;
}

function Quizzes() {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { res, callAPI } = useAuthenticatedFetch({
    url: config.API_URI + `/quiz`,
    method: "GET",
  });
  const navigate = useNavigate();

  useEffect(() => {
    callAPI();
  }, []);

  useEffect(() => {
    if (res.statusCode === 200) {
      setQuizzes(res.data.quizzes);
      setIsLoading(false);
    }
  }, [res]);

  return (
    <SidebarLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Saved Quizzes
          </h1>
          <p className="text-gray-600">
            Access your previously created quizzes and study materials
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm p-6 h-36"
              ></div>
            ))}
          </div>
        ) : quizzes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <h3 className="font-semibold text-xl text-gray-800 mb-2">
              No quizzes yet
            </h3>
            <p className="text-gray-600 mb-6">
              Create your first quiz using one of our AI tools
            </p>
            <button
              onClick={() => navigate("/tools/quiz")}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-800 rounded-md hover:shadow-md transition-all"
            >
              Create a Quiz <BsArrowRight className="ml-2" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {quizzes.map((quiz) => (
              <div
                key={quiz.id}
                onClick={() => navigate(`/quiz/${quiz.id}`)}
                className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 flex justify-between items-center">
                  <div className="text-xs text-gray-500">
                    {quiz.timeCreated
                      ? format(new Date(quiz.timeCreated), "MMM d, yyyy")
                      : "No date"}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <BsPencil className="text-gray-400" />
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-medium text-lg text-gray-800 mb-2 truncate">
                    {quiz.name}
                  </h3>
                  <div className="flex items-center text-sm text-yellow-600 mt-4 group-hover:translate-x-1 transition-transform duration-200">
                    View Quiz <BsArrowRight className="ml-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default Quizzes;
